import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, X, FileText, Calendar, Upload, File } from 'lucide-react';
import { useCommuniques } from '../../hooks/useCommuniques';
import AdminLayout from '../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: #004d8f;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$category) {
      case 'Institution': return '#FEE2E2';
      case 'Activités': return '#DBEAFE';
      case 'Événement': return '#D1FAE5';
      case 'Partenariat': return '#FEF3C7';
      case 'Sensibilisation': return '#F3E8FF';
      case 'Rapport': return '#FED7AA';
      case 'Formation': return '#E0F2FE';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$category) {
      case 'Institution': return '#DC2626';
      case 'Activités': return '#2563EB';
      case 'Événement': return '#059669';
      case 'Partenariat': return '#D97706';
      case 'Sensibilisation': return '#9333EA';
      case 'Rapport': return '#C2410C';
      case 'Formation': return '#0369A1';
      default: return '#6B7280';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--primary-blue);
  color: white;
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: #004d8f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--background-light);
  color: var(--text-dark);
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: var(--border-light);
  }
`;

const CloseButton = styled.button`
  background: none;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const ErrorMessage = styled.div`
  background: #FEE2E2;
  color: #DC2626;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const ErrorHelp = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #FEF3C7;
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  color: #92400E;
  
  code {
    background: #FDE68A;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-family: monospace;
  }
`;

const UploadBox = styled.div`
  border: 2px dashed ${props => props.$hasFile ? 'var(--primary-blue)' : 'var(--border-light)'};
  border-radius: var(--radius-md);
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  background: ${props => props.$hasFile ? 'rgba(0, 38, 100, 0.05)' : 'var(--background-light)'};
  
  &:hover {
    border-color: var(--primary-blue);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #E0F2FE;
  border-radius: var(--radius-md);
  margin-top: 0.5rem;
  color: #0369A1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--border-light);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary-blue);
  width: ${props => props.$progress}%; ? transition : width 0.3s ease;
`;

const categories = [
  'Institution',
  'Activités',
  'Événement',
  'Partenariat',
  'Sensibilisation',
  'Rapport',
  'Formation'
];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Octets';
  const k = 1024;
  const sizes = ['Octets', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AdminCommuniques = () => {
  const { communiques, loading, uploadProgress, createCommunique, editCommunique, removeCommunique } = useCommuniques();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCommunique, setEditingCommunique] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: categories[0],
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Veuillez entrer un titre');
      return;
    }

    if (!editingCommunique && !pdfFile) {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    let result;
    if (editingCommunique) {
      result = await editCommunique(editingCommunique.id, formData, pdfFile, !!pdfFile);
    } else {
      result = await createCommunique(formData, pdfFile);
    }

    if (result.success) {
      closeModal();
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (id, pdfPath) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce communiqué ')) {
      await removeCommunique(id, pdfPath);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    setPdfFile(file);
    setError('');
  };

  const openModal = (communique = null) => {
    if (communique) {
      setEditingCommunique(communique);
      setFormData({
        title: communique.title,
        category: communique.category,
      });
      setPdfFile(null);
    } else {
      setEditingCommunique(null);
      setFormData({
        title: '',
        category: categories[0],
      });
      setPdfFile(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCommunique(null);
    setPdfFile(null);
    setError('');
  };

  return (
    <AdminLayout title="Communiqués">
      <Container>
        <Header>
          <Title>Gestion des Communiqués</Title>
          <AddButton onClick={() => openModal()}>
            <Plus size={20} />
            Ajouter un communiqué
          </AddButton>
        </Header>

        <Table>
          <thead>
            <tr>
              <Th>Titre</Th>
              <Th>Catégorie</Th>
              <Th>Date</Th>
              <Th>Fichier</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>Chargement...</Td>
              </tr>
            ) : communiques.length === 0 ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>Aucun communiqué enregistré</Td>
              </tr>
            ) : (
              communiques.map((communique) => (
                <tr key={communique.id}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} />
                      {communique.title}
                    </div>
                  </Td>
                  <Td>
                    <CategoryBadge $category={communique.category}>
                      {communique.category}
                    </CategoryBadge>
                  </Td>
                  <Td>
                    {communique.date.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </Td>
                  <Td>
                    {communique.pdfUrl ? (
                      <span style={{ color: '#059669', fontSize: '0.9rem' }}>✓ PDF</span>
                    ) : (
                      <span style={{ color: '#DC2626', fontSize: '0.9rem' }}>✗ Aucun</span>
                    )}
                  </Td>
                  <Td>
                    <ActionButton onClick={() => openModal(communique)}>
                      <Edit2 size={18} />
                    </ActionButton>
                    <ActionButton className="delete" onClick={() => handleDelete(communique.id, communique.pdfPath)}>
                      <Trash2 size={18} />
                    </ActionButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {isModalOpen && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingCommunique ? 'Modifier' : 'Ajouter'} un communiqué</h2>
                <CloseButton onClick={closeModal}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                {error && (
                  <ErrorMessage>
                    {error}
                    {(error.includes('autorisé') || error.includes('CORS') || error.includes('permission')) && (
                      <ErrorHelp>
                        <strong>Solution :</strong><br/>
                        1. Allez dans <a href="https://console.firebase.google.com/project/mediature-81135/storage/rules" target="_blank" rel="noopener noreferrer">Firebase Console &gt; Storage &gt; Rules</a><br/>
                        2. Collez les règles du fichier <code>storage.rules</code><br/>
                        3. Cliquez sur <strong>"Publier"</strong><br/>
                        4. Rafraîchissez cette page
                      </ErrorHelp>
                    )}
                  </ErrorMessage>
                )}

                <FormGroup>
                  <Label>Titre</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Titre du communiqué"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Catégorie</Label>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Fichier PDF {editingCommunique && '(laisser vide pour garder le fichier actuel)'}</Label>
                  <UploadBox 
                    $hasFile={!!pdfFile}
                    onClick={() => document.getElementById('pdf-input').click()}
                  >
                    <Input
                      id="pdf-input"
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {pdfFile ? (
                      <FileInfo>
                        <File size={20} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{pdfFile.name}</div>
                          <small>{formatFileSize(pdfFile.size)}</small>
                        </div>
                      </FileInfo>
                    ) : (
                      <>
                        <Upload size={40} color="var(--text-light)" style={{ marginBottom: '0.5rem' }} />
                        <div>Cliquez pour sélectionner un PDF</div>
                        <small style={{ color: 'var(--text-light)' }}>
                          Aucune limite de taille
                        </small>
                      </>
                    )}
                  </UploadBox>
                </FormGroup>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <FormGroup>
                    <Label>Upload en cours...</Label>
                    <ProgressBar>
                      <ProgressFill $progress={uploadProgress} />
                    </ProgressBar>
                  </FormGroup>
                )}

                <ButtonGroup>
                  <CancelButton type="button" onClick={closeModal}>
                    Annuler
                  </CancelButton>
                  <SubmitButton type="submit" disabled={uploadProgress > 0}>
                    {uploadProgress > 0 ? 'Upload en cours...' : (editingCommunique ? 'Modifier' : 'Publier')}
                  </SubmitButton>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminCommuniques;
